import { useNavigate } from '@tanstack/react-router'
import {
  ColumnDef,
  PaginationState,
  Row,
  SortingState,
} from '@tanstack/react-table'
import { CantileverButton } from 'components/CantileverButton'
import { ConfirmDeletePopup } from 'components/ConfirmDeletePopup'
import { TextCell } from 'components/table/cells/TextCell'
import { TableComponent } from 'components/table/TableComponent'
import { format } from 'date-fns'
import { useDetectionConfigurationMutation } from 'features/anomalyDetection'
import { useDetectionConfigurations } from 'features/anomalyDetection/hooks/useDetectionConfigurations'
import { useGeckoNotifications } from 'features/notifications'
import { X } from 'lucide-react'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'
import { DetectionConfigurationLoadedEntry, InputType } from 'types'

const RemoveButtonCell = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`

const StyledX = styled(X)`
  height: 1rem;
  width: 1rem;
  color: ${({ theme }) => theme.forecast.danger};
`

interface DetectionConfigurationTableProps {
  siteID: string
}

export const DetectionConfigurationTable: React.FC<
  DetectionConfigurationTableProps
> = ({ siteID }) => {
  const navigate = useNavigate()
  const { delete: deleteDetectionConfiguration } =
    useDetectionConfigurationMutation()
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  })
  const [sorting, setSorting] = useState<SortingState>([])
  const [showConfirmDelete, setShowConfirmDelete] = useState(false)
  const [deletePopupPosition, setDeletePopupPosition] = useState({
    top: 0,
    left: 0,
  })
  const [entryToDelete, setEntryToDelete] =
    useState<DetectionConfigurationLoadedEntry | null>(null)

  const { errorNotification, successNotification } = useGeckoNotifications()
  const { detectionConfigurations, isLoading, mutate } =
    useDetectionConfigurations()

  const handleDeleteClick = useCallback(
    (entry: DetectionConfigurationLoadedEntry) =>
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        const rect = event.currentTarget.getBoundingClientRect()
        setDeletePopupPosition({
          top: rect.bottom + 4,
          left: rect.left + 16,
        })
        setEntryToDelete(entry)
        setShowConfirmDelete(true)
      },
    []
  )

  const confirmDelete = () => {
    if (!entryToDelete) return

    deleteDetectionConfiguration({ configurationId: entryToDelete.entry.id })
      .then(() => {
        setShowConfirmDelete(false)
        setEntryToDelete(null)
        successNotification('Detection configuration deleted successfully')
        mutate()
      })
      .catch((error) => {
        errorNotification(
          `Failed to delete detection configuration: ${error.message}`
        )
      })
  }

  const cancelDelete = () => {
    setShowConfirmDelete(false)
    setEntryToDelete(null)
  }

  const columns = useMemo<ColumnDef<DetectionConfigurationLoadedEntry>[]>(
    () => [
      {
        id: 'remove',
        header: '',
        cell: ({ row }) => (
          <RemoveButtonCell>
            <CantileverButton
              iconOnly
              onClick={handleDeleteClick(row.original)}>
              <StyledX />
            </CantileverButton>
          </RemoveButtonCell>
        ),
      },
      {
        accessorKey: 'entry.id',
        id: 'id',
        header: 'Configuration ID',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => <TextCell truncate text={row.original.entry.id} />,
      },
      {
        accessorKey: 'entry.data.name',
        id: 'name',
        header: 'Name',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell truncate text={row.original.entry.data.name} />
        ),
      },
      {
        accessorKey: 'entry.data.last_polled_at',
        id: 'lastPolledAt',
        header: 'Last Polled At (UTC)',
        sortingFn: 'datetime',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Date,
        },
        cell: ({ row }) => {
          const lastPolledAt = row.original.entry.data.last_polled_at
          if (lastPolledAt && typeof lastPolledAt === 'string') {
            const date = new Date(lastPolledAt)
            return <TextCell text={format(date, 'HH:mm, MMM dd, yyyy')} />
          }
          return <TextCell text='-' />
        },
      },
      {
        accessorKey: 'entry.data.is_enabled',
        id: 'isEnabled',
        header: 'Enabled',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell text={row.original.entry.data.is_enabled ? 'Yes' : 'No'} />
        ),
      },
      {
        accessorKey: 'entry.data.polling_frequency_minutes',
        id: 'pollingFrequency',
        header: 'Polling Frequency (mins)',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: true,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell
            text={row.original.entry.data.polling_frequency_minutes.toString()}
          />
        ),
      },
      {
        accessorKey: 'entry.data.detection_models',
        id: 'detectionModels',
        header: 'Detection Models',
        sortingFn: 'alphanumeric',
        sortUndefined: 'last',
        filterFn: 'includesString',
        enableSorting: false,
        meta: {
          inputType: InputType.Text,
        },
        cell: ({ row }) => (
          <TextCell
            truncate
            text={row.original.entry.data.detection_models
              .map((model) => model.model_name)
              .join(', ')}
          />
        ),
      },
    ],
    [handleDeleteClick]
  )

  const handleRowClick = (row: Row<DetectionConfigurationLoadedEntry>) => {
    navigate({
      to: '/anomaly-detection/$siteID/$detectionConfigurationId',
      params: { siteID, detectionConfigurationId: row.original.entry.id },
    })
  }

  return (
    <>
      <TableComponent
        data={detectionConfigurations}
        isLoadingData={isLoading}
        columns={columns}
        footerName='Detection Configurations'
        pagination={pagination}
        setPagination={setPagination}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={handleRowClick}
        showFilterBar={true}
        showTable={true}
      />
      {showConfirmDelete && (
        <ConfirmDeletePopup
          entityName='detection configuration'
          deleteMessage='Are you sure you want to delete this detection configuration?'
          popupPosition={deletePopupPosition}
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
        />
      )}
    </>
  )
}
